/**
 * The artist duplicate auditor's vocabulary and the readers for one finding.
 *
 * `@/types/tina` owns the wire shapes; this module owns what the curator surface needs on
 * top of them — labels, the per-kind hint, the one-line summary a row is judged from, and
 * the deep links out of a finding. Same split and the same `Record<Union, string>`
 * discipline as `@/lib/tina/venue-audit`.
 *
 * **What the card is for.** A curator decides whether two records are one act, then goes
 * and merges them ON THE ARTIST'S PAGE. So every line here carries what that decision
 * needs — followers, gigs, venues, who created the record — and links to both artists.
 * There is no merge here and nothing that pretends to be one. Spec section 9.2.
 *
 * Every render path degrades instead of throwing: `kind` arrives as wire data from an
 * orchestrator deploy that may be newer than this build, so an unknown kind falls back to
 * the fingerprint.
 */
import dayjs from "dayjs";

import {
  ARTIST_AUDIT_KINDS,
  ARTIST_AUDIT_SEVERITIES,
  ARTIST_AUDIT_STATUSES,
  type ArtistAuditEvidenceArtist,
  type ArtistAuditFindingRow,
  type ArtistAuditKind,
  type ArtistAuditSeverity,
  type ArtistAuditStatus,
} from "@/types/tina";

/**
 * The tag Approve writes into the Mood artist's `tags`, for the frontend to read. Approve
 * adds it to the tags already there; it never replaces the list.
 */
export const DECEASED_ARTIST_TAG = "deceased";

/** Short label for a kind — what the "What" column and the count strip show. */
export const ARTIST_AUDIT_KIND_LABEL: Record<ArtistAuditKind, string> = {
  duplicate_spotify_id: "One Spotify profile, two records",
  duplicate_name: "Same name, two profiles",
  merge_state_broken: "Broken merge",
  merged_artist_on_live_lineup: "Merged artist still billed",
  deceased_artist_with_future_events: "Possibly deceased",
};

/**
 * What the curator is being asked to judge, per kind. Shown once above the table: the
 * first backlog is ~1,400 findings of which ~1,240 are one kind, so the question is read
 * once and answered many times.
 */
export const ARTIST_AUDIT_KIND_HINT: Record<ArtistAuditKind, string> = {
  duplicate_spotify_id:
    "Two live artist records point at the same Spotify profile. One profile is one act, so this is not a judgement call — pick which record survives, open it, and merge the other into it. Dismiss only if the Spotify link on one of them is simply wrong.",
  duplicate_name:
    "Two live records share a name but point at two Spotify profiles. Two real acts can share a name, so read the evidence: followers, gigs, the venues they have in common, who created each. High means a multi-word name with both sides booked; low means a one-word name or an unbooked side, where honest namesakes live. If they are one act, merge on the artist page; if not, dismiss.",
  merge_state_broken:
    "A merged artist's redirect points at itself, at a record that does not exist, or at another merged record. The dangling case is the worst: the act is hidden AND unreachable. Fix the redirect on the artist page.",
  merged_artist_on_live_lineup:
    "An artist that was merged away is still on a live event's lineup, so the app shows an act the catalog says is gone. Open the event and replace it with the surviving artist.",
  deceased_artist_with_future_events:
    "Possibly deceased — check. Wikidata says the person behind this artist's Spotify profile died recently, yet they are billed on future events. Most of these are fine: a tribute or birthday night, or a composer's work being performed — Dismiss those. If an event bills the act as if it will perform, it came from a stale page: fix or cancel the event. Verify on Wikidata first; it is open-edit and can be wrong.",
};

export const artistAuditKindLabel = (kind: string): string =>
  kind in ARTIST_AUDIT_KIND_LABEL
    ? ARTIST_AUDIT_KIND_LABEL[kind as ArtistAuditKind]
    : kind;

export const ARTIST_AUDIT_KIND_OPTIONS: {
  value: ArtistAuditKind;
  label: string;
}[] = ARTIST_AUDIT_KINDS.map((kind) => ({
  value: kind,
  label: ARTIST_AUDIT_KIND_LABEL[kind],
}));

export const ARTIST_AUDIT_STATUS_LABEL: Record<ArtistAuditStatus, string> = {
  open: "Open",
  dismissed: "Dismissed",
  approved: "Approved (deceased)",
  resolved: "Fixed itself",
  all: "Everything",
};
export const ARTIST_AUDIT_STATUS_OPTIONS: {
  value: ArtistAuditStatus;
  label: string;
}[] = ARTIST_AUDIT_STATUSES.map((status) => ({
  value: status,
  label: ARTIST_AUDIT_STATUS_LABEL[status],
}));

export const ARTIST_AUDIT_SEVERITY_LABEL: Record<ArtistAuditSeverity, string> =
  {
    high: "High",
    low: "Low",
  };
export const ARTIST_AUDIT_SEVERITY_OPTIONS: {
  value: ArtistAuditSeverity;
  label: string;
}[] = ARTIST_AUDIT_SEVERITIES.map((severity) => ({
  value: severity,
  label: ARTIST_AUDIT_SEVERITY_LABEL[severity],
}));

/** Narrowed on read, never trusted: these come back from `localStorage`. */
export const isArtistAuditKind = (
  value: string | null,
): value is ArtistAuditKind =>
  value !== null && ARTIST_AUDIT_KINDS.includes(value as ArtistAuditKind);
export const isArtistAuditSeverity = (
  value: string | null,
): value is ArtistAuditSeverity =>
  value !== null &&
  ARTIST_AUDIT_SEVERITIES.includes(value as ArtistAuditSeverity);
export const isArtistAuditStatus = (
  value: string | null,
): value is ArtistAuditStatus =>
  value !== null && ARTIST_AUDIT_STATUSES.includes(value as ArtistAuditStatus);

/** Where a curator goes to look at — and merge — the artist itself. */
export const artistAuditArtistHref = (artistId: string): string =>
  `/artists/edit/${artistId}`;

/** The Spotify profile, so a curator can check which of two is the real act. */
export const artistAuditSpotifyHref = (
  spotifyId: string | null | undefined,
): string | null =>
  spotifyId ? `https://open.spotify.com/artist/${spotifyId}` : null;

/** The Wikidata page behind a "possibly deceased" card, so a curator can verify it. */
export const artistAuditWikidataHref = (
  url: string | null | undefined,
  qid: string | null | undefined,
): string | null =>
  url && url.startsWith("https://www.wikidata.org/")
    ? url
    : qid
      ? `https://www.wikidata.org/wiki/${qid}`
      : null;

/** The live event still billing a merged artist. */
export const artistAuditEventHref = (
  eventId: string | null | undefined,
): string | null => (eventId ? `/events/edit/${eventId}` : null);

const plural = (n: number, one: string, many: string): string =>
  `${n} ${n === 1 ? one : many}`;

/** `active YYYY-MM → YYYY-MM`, or `active YYYY-MM` when both months are the same. */
const activeWindow = (
  firstLiveAt: string,
  lastLiveAt: string | null | undefined,
): string => {
  const from = dayjs(firstLiveAt).format("YYYY-MM");
  const to = dayjs(lastLiveAt ?? firstLiveAt).format("YYYY-MM");
  return from === to ? `active ${from}` : `active ${from} → ${to}`;
};

/**
 * One artist, with the numbers a merge decision turns on.
 *
 * Followers first because it is usually the tie-breaker for which record survives; then
 * the bookings; then who made the record — a Tina site name when Tina did, else the
 * creator id, because "both made by `bizouk` a week apart" is itself evidence.
 */
export const artistAuditArtistLine = (
  artist: ArtistAuditEvidenceArtist,
): string => {
  const name = artist.name || artist.artist_id;
  const gigs = `${plural(artist.live_events, "gig", "gigs")} at ${plural(artist.live_venues, "venue", "venues")}`;
  const origin = artist.tina_site ?? artist.created_by ?? "";
  const created = artist.created_at
    ? `created ${dayjs(artist.created_at).format("YYYY-MM-DD")}`
    : "";
  const active = artist.first_live_at
    ? activeWindow(artist.first_live_at, artist.last_live_at)
    : "";
  const absorbed =
    artist.other_spotify_ids && artist.other_spotify_ids.length > 0
      ? `absorbed ${plural(artist.other_spotify_ids.length, "id", "ids")}`
      : "";
  const parts = [
    `${plural(artist.followers, "follower", "followers")}`,
    gigs,
    origin,
    created,
    active,
    absorbed,
  ].filter((part) => part.length > 0);
  return `${name} — ${parts.join(" · ")}`;
};

const describeMergeShape = (row: ArtistAuditFindingRow): string => {
  const target = row.evidence.merge_target_id ?? "?";
  switch (row.evidence.merge_shape) {
    case "self":
      return "merged into itself";
    case "dangling":
      return `merged into ${target}, which does not exist — the act is hidden and unreachable`;
    case "chain":
      return `merged into ${target}, which is itself merged — the redirect should point at the final target`;
    default:
      return `merged into ${target}`;
  }
};

/**
 * The kind-specific half of the summary: the one fact that makes this finding a finding.
 * Returns `""` for a kind this build does not know, so the summary falls through to the
 * fingerprint instead of rendering a hole.
 */
export const artistAuditFindingMetric = (
  row: ArtistAuditFindingRow,
): string => {
  const e = row.evidence;
  switch (row.kind) {
    case "duplicate_spotify_id":
      return `both point at Spotify ${e.shared_spotify_id ?? "?"} — one profile is one act`;
    case "duplicate_name": {
      // `event_counts_known === false` is the one case where a zero on the card is a
      // hole in our data and not a fact about the act. Say so first, or a curator reads
      // "0 gigs" as "not a real act" and dismisses a duplicate.
      if (e.event_counts_known === false) {
        return "gig counts could not be read for this pair — the zeroes are unknowns, not facts";
      }
      const shared = e.shared_venue_count ?? 0;
      const venues =
        shared > 0
          ? `${plural(shared, "venue", "venues")} in common`
          : "no venues in common";
      const booked = e.both_sides_booked
        ? "both booked"
        : "one side has no gigs";
      return `${venues} · ${booked} · ${plural(e.name_tokens ?? 0, "word", "words")} in the name`;
    }
    case "merge_state_broken":
      return describeMergeShape(row);
    case "deceased_artist_with_future_events": {
      const died = e.died_on ? `died ${e.died_on}` : "date of death unknown";
      const source = e.wikidata_qid ? ` per Wikidata ${e.wikidata_qid}` : " per Wikidata";
      const count = e.future_event_count ?? e.future_events?.length ?? 0;
      return `${died}${source} · ${plural(count, "future event", "future events")} — tribute/composer = Dismiss`;
    }
    case "merged_artist_on_live_lineup": {
      const base = `still billed on ${e.event_name || e.event_id || "a live event"}`;
      return typeof e.live_event_count === "number" && e.live_event_count > 1
        ? `${base} · one of ${e.live_event_count} live events`
        : base;
    }
    default:
      return "";
  }
};

/**
 * One line a curator can judge a row from without opening anything: every artist with
 * its numbers, then the kind's own metric. Falls back to the fingerprint when it has
 * neither.
 */
export const summariseArtistFinding = (row: ArtistAuditFindingRow): string => {
  const artists = (row.evidence.artists ?? [])
    .map(artistAuditArtistLine)
    .join("  ·  ");
  const parts = [artists, artistAuditFindingMetric(row)].filter(
    (part) => part.length > 0,
  );
  return parts.length > 0 ? parts.join("  ·  ") : row.fingerprint;
};
